// file path: src\Components\AuthGuard.tsx

import React, { Suspense, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getProfile } from '../services/auth.service';
import { setUserData, setToken, setUserDataLoading, setSubscription, setIsSubClient, setCompany } from '../stores/auth.slice';
import LoaderComponent from './LoaderComponent';
import { RootState, AppDispatch } from '../stores/app.store'; // Adjust the path as needed

// Lazy load the route components
const PrivateRoutes = React.lazy(() => import('../Routes/PrivateRoutes'));
const PublicRoutes = React.lazy(() => import('../Routes/PublicRoutes'));

const AuthGuard: React.FC = () => {
    const token = useSelector((state: RootState) => state.auth.token);
    const userDataLoading = useSelector((state: RootState) => state.auth.userDataLoading);
    const dispatch = useDispatch<AppDispatch>();

    useEffect(() => {
        // If there's no token and user is trying to visit a protected URL,
        // save that URL to localStorage so we can redirect after login/register.
        // We only save once to avoid overwriting an existing redirect.
        if (!token) {
            try {
                const pathname = window.location.pathname || '/';
                const search = window.location.search || '';
                const fullPath = `${pathname}${search}`;
                const publicPaths = ['/', '/register', '/forgot-password'];
                const normalizedPath = pathname.split('?')[0];
                if (!publicPaths.includes(normalizedPath)) {
                    window.localStorage.setItem('post_login_redirect', fullPath);
                    // Replace history to the login root so the public router shows the login page
                    // without doing a hard reload.
                    const redirectUrl = search.includes('getting_started=1') ? '/register' : '/'
                    // console.log('Post Login Redirect :', { redirectUrl, search, pathname });
                    window.history.replaceState(null, '', redirectUrl);
                }
            } catch (e) {
                // ignore any cross-origin or unexpected errors when accessing location/localStorage
                console.error('AuthGuard redirect save error', e);
            }
        }

        const timer = setTimeout(() => {
            if (token) {
                dispatch(setUserDataLoading(true));
                getProfile(token).then((res) => {
                    if (res.data) {
                        const { subscription, isSubClient, company } = res.data
                        if (subscription) {
                            dispatch(setSubscription(subscription));
                        }
                        if (company) {
                            dispatch(setCompany(company));
                        }
                        if (isSubClient !== undefined) {
                            dispatch(setIsSubClient(isSubClient));
                        }
                        dispatch(setUserData(res.data));
                    }
                }).catch((err) => {
                    if (err.response?.status === 401) {
                        window.localStorage.removeItem('token');
                        dispatch(setUserData(null));
                        dispatch(setToken(null));
                    } else {
                        console.error("Get User Details Error", err);
                    }
                }).finally(() => {
                    dispatch(setUserDataLoading(false));
                });
            }
        }, 1)
        return () => {
            clearTimeout(timer)
        }
    }, [token]);

    if (userDataLoading) {
        return <LoaderComponent msg="Verifying Session..." />;
    }

    return (
        <Suspense fallback={<LoaderComponent msg="Loading Assets..." />}>
            {!token ? <PublicRoutes /> : <PrivateRoutes />}
        </Suspense>
    );
};

export default AuthGuard;
